import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, FormGroup, FormText, Modal, ModalBody, ModalHeader } from "reactstrap";
import { PathwayInput as Input } from "../../../_UI/StyledComponents/PathwayForm";
import AlertComponent from "../../../_UI/Components/Alerts/AlertComponent";
import { LoadingSpinnerButton, PathwayButton } from "../../../_UI/Components/PathwayButtons";
import { adminAreaActions } from "../../services/store/adminarea.actions";
import { getSessionId } from "../../../_services/session";
import PathwayDropzone from "../../../_UI/Components/PathwayDropzone";
import styled from "styled-components";
import { PathwayLabel } from "../../../_UI/StyledComponents/PathwayForm";

const TemplateFormGroup = styled(FormGroup)`
    margin-bottom: var(--gap-xl);
`;

const TemplateLabelLead = styled.span`
    font-weight: var(--pw-weight-semibold);
    color: var(--text-heading);
`;

const TemplateLabelHint = styled.span`
    font-weight: var(--pw-weight-light);
    color: var(--text-muted);
    line-height: var(--pw-leading-normal);
`;

// Holds the panel and the feedback of the one-click flow together, so the outcome is read where the
// button was pressed instead of at the bottom of the form.
const QuickReportSection = styled.div`
    display: grid;
    row-gap: var(--gap-m);
    margin-bottom: var(--gap-xl);
`;

const QuickReportPanel = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--gap-m);
    padding: var(--gap-m);
    border: 1px solid var(--border-hairline);
    border-radius: var(--pw-radius-lg);
    background: var(--surface-tile);
`;

const QuickReportTitle = styled.div`
    font-weight: var(--pw-weight-semibold);
    color: var(--text-heading);
`;

const QuickReportHint = styled.div`
    color: var(--text-muted);
    line-height: var(--pw-leading-normal);
`;

// The request usually fails after GitLab has created the issue - it is the follow-up calls that time out -
// so "erneut versuchen" would file a duplicate.
const BUG_REPORT_ERROR_HINT =
    "Das Ticket wurde in GitLab in der Regel trotzdem erstellt – bitte dort prüfen, bevor erneut gemeldet wird.";

const pad = (value) => String(value).padStart(2, "0");

/**
 * Title of a one-click performance report. The timestamp is the local time of the reporter — that is what
 * correlates with the slowness they just saw, a server-side timestamp would not.
 */
export const buildPerformanceReportTitle = (url, date = new Date()) =>
    `Performance - ${url} - ${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}` +
    ` ${pad(date.getHours())}:${pad(date.getMinutes())}`;

export const ReportBugModal = ({ isModalOpen, bugReportLoading, bugReportResult, bugReportError, organizationUUID, dispatch, milestones, milestonesLoading, projectLabels, projectLabelsLoading }) => {
    const [bugTitle, setBugTitle] = useState("");
    const [bugDescription, setBugDescription] = useState("");
    const [expectedBehavior, setExpectedBehavior] = useState("");
    const [stepsToReproduce, setStepsToReproduce] = useState("");
    const [selectedMilestoneId, setSelectedMilestoneId] = useState("");
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [labelSearch, setLabelSearch] = useState("");
    const [labelDropdownOpen, setLabelDropdownOpen] = useState(false);
    const [screenshots, setScreenshots] = useState([]);
    const [useGitlabTemplate, setUseGitlabTemplate] = useState(false);
    const [templateContent, setTemplateContent] = useState("");
    // Which of the two flows is currently in flight — both share bugReportLoading, only the button that
    // was pressed should spin.
    const [performanceSubmitting, setPerformanceSubmitting] = useState(false);
    const labelDropdownRef = useRef(null);
    // Only auto-fetch once per opening of the modal — an empty list is a valid
    // result, and retrying on it would loop forever (the reducer resets the list
    // to null on request, so the effect deps change on every response).
    const milestonesRequested = useRef(false);
    const projectLabelsRequested = useRef(false);

    // Fetch once per opening, keyed only on isModalOpen. Do NOT depend on
    // milestones/projectLabels: the guard was on their length, so an empty or
    // failed response rewrote the array, the dependency identity changed, the
    // guard was still true and it dispatched again — an endless request loop.
    useEffect(() => {
        if (!isModalOpen) {
            milestonesRequested.current = false;
            projectLabelsRequested.current = false;
            return;
        }
        if (useGitlabTemplate) return;
        if (!milestonesRequested.current && !milestonesLoading && !milestones?.length) {
            milestonesRequested.current = true;
            dispatch(adminAreaActions.getActiveMilestones());
        }
        if (!projectLabelsRequested.current && !projectLabelsLoading && !projectLabels?.length) {
            projectLabelsRequested.current = true;
            dispatch(adminAreaActions.getProjectLabels());
        }
    }, [isModalOpen, useGitlabTemplate, dispatch, milestones, milestonesLoading, projectLabels, projectLabelsLoading]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (labelDropdownRef.current && !labelDropdownRef.current.contains(e.target)) {
                setLabelDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredLabels = useMemo(() => {
        if (!projectLabels) return [];
        if (!labelSearch.trim()) return projectLabels;
        const search = labelSearch.toLowerCase();
        return projectLabels.filter((l) => l.name.toLowerCase().includes(search));
    }, [projectLabels, labelSearch]);

    const toggleLabel = (labelName) => {
        setSelectedLabels((prev) => {
            if (prev.includes(labelName)) {
                return prev.filter((l) => l !== labelName);
            }
            setLabelSearch("");
            return [...prev, labelName];
        });
    };

    const getLabelObj = (name) => projectLabels?.find((l) => l.name === name);

    const addImageFiles = (files) => {
        const imageFiles = files.filter((f) => f.type.startsWith("image/"));
        if (imageFiles.length > 0) {
            setScreenshots((prev) => [...prev, ...imageFiles]);
        }
    };






    const removeScreenshot = (index) => {
        setScreenshots((prev) => prev.filter((_, i) => i !== index));
    };

    const handlePerformanceReport = () => {
        if (bugReportLoading) return;
        setPerformanceSubmitting(true);

        const currentUrl = window.location.href;
        const requestData = {
            organizationUUID,
            bugTitle: buildPerformanceReportTitle(currentUrl),
            currentUrl,
            sessionId: getSessionId(),
            performanceReport: true,
        };

        const formData = new FormData();
        formData.append("request", new Blob([JSON.stringify(requestData)], { type: "application/json" }));

        dispatch(adminAreaActions.createBugReport(formData));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!bugTitle.trim()) return;
        setPerformanceSubmitting(false);

        const requestData = {
            organizationUUID,
            bugTitle: bugTitle.trim(),
            bugDescription: useGitlabTemplate ? null : bugDescription.trim() || null,
            expectedBehavior: useGitlabTemplate ? null : expectedBehavior.trim() || null,
            stepsToReproduce: useGitlabTemplate ? null : stepsToReproduce.trim() || null,
            currentUrl: window.location.href,
            sessionId: getSessionId(),
            milestoneId: useGitlabTemplate || !selectedMilestoneId ? null : parseInt(selectedMilestoneId, 10),
            labels: useGitlabTemplate || selectedLabels.length === 0 ? null : selectedLabels,
            useGitlabTemplate,
            templateContent: useGitlabTemplate ? templateContent.trim() || null : null,
        };

        const formData = new FormData();
        formData.append("request", new Blob([JSON.stringify(requestData)], { type: "application/json" }));
        screenshots.forEach((file) => {
            formData.append("screenshots", file);
        });

        dispatch(adminAreaActions.createBugReport(formData));
    };

    const handleClose = () => {
        setBugTitle("");
        setBugDescription("");
        setExpectedBehavior("");
        setStepsToReproduce("");
        setSelectedMilestoneId("");
        setSelectedLabels([]);
        setLabelSearch("");
        setLabelDropdownOpen(false);
        setScreenshots([]);
        setUseGitlabTemplate(true);
        setTemplateContent("");
        setPerformanceSubmitting(false);
        dispatch(adminAreaActions.toggleBugReportModal());
    };

    const bugReportErrorText =
        bugReportError && !bugReportResult ? `${bugReportError}\n${BUG_REPORT_ERROR_HINT}` : null;

    return (
        <Modal
            className="item-added-modal"
            isOpen={isModalOpen}
            toggle={bugReportLoading ? undefined : handleClose}
            backdrop={bugReportLoading ? "static" : true}
            keyboard={!bugReportLoading}
            size="lg"
        >
            <ModalHeader toggle={bugReportLoading ? undefined : handleClose}>Bug melden</ModalHeader>
            <ModalBody>
                {!bugReportResult && (
                    <>
                    <QuickReportSection data-cy="performance-report-section">
                    <QuickReportPanel>
                        <div>
                            <QuickReportTitle>Performance-Problem</QuickReportTitle>
                            <QuickReportHint>
                                Erstellt mit einem Klick ein internes Ticket f{"ü"}r die aktuelle Seite {"–"}{" "}
                                inklusive URL und Session-ID. Keine weitere Eingabe n{"ö"}tig.
                            </QuickReportHint>
                        </div>
                        <LoadingSpinnerButton
                            loading={!!bugReportLoading && performanceSubmitting}
                            disabled={!!bugReportLoading}
                            color="warning"
                            onClick={handlePerformanceReport}
                            buttonCyData="report-performance-issue"
                        >
                            <i className="fa fa-tachometer" style={{ marginRight: "0.5rem" }} aria-hidden="true" />
                            Performance melden
                        </LoadingSpinnerButton>
                    </QuickReportPanel>
                    {performanceSubmitting && bugReportErrorText && (
                        <AlertComponent errorMessages={bugReportErrorText} style={{ marginBottom: 0 }} />
                    )}
                    </QuickReportSection>
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <PathwayLabel htmlFor="bugTitle">Titel *</PathwayLabel>
                            <Input
                                id="bugTitle"
                                name="bugTitle"
                                placeholder="Bug-Titel eingeben"
                                type="text"
                                value={bugTitle}
                                onChange={(e) => setBugTitle(e.target.value)}
                                required
                            />
                        </FormGroup>
                        <TemplateFormGroup check>
                            <PathwayLabel check>
                                <Input
                                    type="checkbox"
                                    checked={useGitlabTemplate}
                                    onChange={(e) => setUseGitlabTemplate(e.target.checked)}
                                />{" "}
                                <TemplateLabelLead>Gemäß GitLab-Vorlage verwenden</TemplateLabelLead>{" "}
                                <TemplateLabelHint>
                                    Wenn aktiviert, werden Beschreibung, Expected Behavior, Steps to Reproduce und
                                    Labels über die GitLab-Vorlage und Quick Actions gesetzt.
                                </TemplateLabelHint>
                            </PathwayLabel>
                        </TemplateFormGroup>
                        {useGitlabTemplate && (
                            <FormGroup>
                                <PathwayLabel htmlFor="templateContent">GitLab Issue-Inhalt (Vorlage)</PathwayLabel>
                                <Input
                                    id="templateContent"
                                    name="templateContent"
                                    type="textarea"
                                    rows={16}
                                    style={{ fontFamily: "monospace", fontSize: "13px" }}
                                    value={templateContent}
                                    onChange={(e) => setTemplateContent(e.target.value)}
                                    placeholder={`# Bug Report AI Template\n\n## Bug Summary\n...\n\n## Labels & Assignments\n/label ~Bug\n/label ~"Status::Backlog"\n/milestone %"1.9.29"\n/assign @marcel.bauer`}
                                />
                                <FormText color="muted">
                                    Den ausgef{"ü"}llten Inhalt der GitLab-Vorlage hier einf{"ü"}gen. Quick Actions wie <code>/label</code>, <code>/milestone</code> oder <code>/assign</code> werden von GitLab automatisch ausgef{"ü"}hrt.
                                </FormText>
                            </FormGroup>
                        )}
                        {!useGitlabTemplate && (
                        <>
                        <FormGroup>
                            <PathwayLabel htmlFor="bugDescription">Beschreibung</PathwayLabel>
                            <Input
                                id="bugDescription"
                                name="bugDescription"
                                placeholder="Optionale Beschreibung"
                                type="textarea"
                                rows={4}
                                value={bugDescription}
                                onChange={(e) => setBugDescription(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <PathwayLabel htmlFor="expectedBehavior">Expected Behavior</PathwayLabel>
                            <Input
                                id="expectedBehavior"
                                name="expectedBehavior"
                                placeholder={`Was w\u00E4re das erwartete Verhalten?`}
                                type="textarea"
                                rows={3}
                                value={expectedBehavior}
                                onChange={(e) => setExpectedBehavior(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <PathwayLabel htmlFor="stepsToReproduce">Steps to Reproduce</PathwayLabel>
                            <Input
                                id="stepsToReproduce"
                                name="stepsToReproduce"
                                placeholder="Schritte zum Reproduzieren des Bugs"
                                type="textarea"
                                rows={3}
                                value={stepsToReproduce}
                                onChange={(e) => setStepsToReproduce(e.target.value)}
                            />
                        </FormGroup>
                        </>
                        )}
                        <FormGroup>
                            <PathwayLabel>Screenshots</PathwayLabel>
                            <PathwayDropzone onFiles={addImageFiles} dataCy="bug-screenshots" />
                            {screenshots.length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
                                    {screenshots.map((file, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                position: "relative",
                                                width: "80px",
                                                height: "80px",
                                                border: "1px solid #ced4da",
                                                borderRadius: "4px",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={file.name}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeScreenshot(index)}
                                                style={{
                                                    position: "absolute",
                                                    top: "2px",
                                                    right: "2px",
                                                    background: "rgba(0,0,0,0.6)",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "50%",
                                                    width: "20px",
                                                    height: "20px",
                                                    fontSize: "12px",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    padding: 0,
                                                }}
                                                title="Entfernen"
                                            >
                                                {"\u00D7"}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </FormGroup>
                        {!useGitlabTemplate && (
                        <>
                        <FormGroup>
                            <PathwayLabel htmlFor="bugMilestone">
                                Milestone{" "}
                                <button
                                    type="button"
                                    onClick={() => dispatch(adminAreaActions.getActiveMilestones())}
                                    style={{ border: "none", background: "none", cursor: "pointer", padding: "0 4px" }}
                                    title="Milestones aktualisieren"
                                    disabled={milestonesLoading}
                                >
                                    <i className={`fa fa-refresh${milestonesLoading ? " fa-spin" : ""}`} style={{ fontSize: "12px", color: "var(--text-subtle)" }} />
                                </button>
                            </PathwayLabel>
                            <Input
                                id="bugMilestone"
                                name="bugMilestone"
                                type="select"
                                value={selectedMilestoneId}
                                onChange={(e) => setSelectedMilestoneId(e.target.value)}
                                disabled={milestonesLoading}
                            >
                                <option value="">
                                    {milestonesLoading ? "Laden..." : "Kein Milestone"}
                                </option>
                                {milestones &&
                                    milestones.map((milestone) => (
                                        <option key={milestone.id} value={milestone.id}>
                                            {milestone.title}
                                        </option>
                                    ))}
                            </Input>
                        </FormGroup>
                        </>
                        )}
                        <FormGroup>
                            <PathwayLabel>
                                Labels{" "}
                                <button
                                    type="button"
                                    onClick={() => dispatch(adminAreaActions.getProjectLabels())}
                                    style={{ border: "none", background: "none", cursor: "pointer", padding: "0 4px" }}
                                    title="Labels aktualisieren"
                                    disabled={projectLabelsLoading}
                                >
                                    <i className={`fa fa-refresh${projectLabelsLoading ? " fa-spin" : ""}`} style={{ fontSize: "12px", color: "var(--text-subtle)" }} />
                                </button>
                            </PathwayLabel>
                            {selectedLabels.length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "8px" }}>
                                    {selectedLabels.map((name) => {
                                        const label = getLabelObj(name);
                                        return (
                                            <span
                                                key={name}
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    padding: "2px 8px",
                                                    borderRadius: "12px",
                                                    fontSize: "12px",
                                                    fontWeight: 500,
                                                    backgroundColor: label?.color || "var(--border-hairline)",
                                                    color: label?.textColor || "#333",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => toggleLabel(name)}
                                                title="Klicken zum Entfernen"
                                            >
                                                {name} <span style={{ marginLeft: "4px", fontWeight: "bold" }}>{"\u00D7"}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            <div ref={labelDropdownRef} style={{ position: "relative" }}>
                                <Input
                                    type="text"
                                    placeholder={projectLabelsLoading ? "Laden..." : "Labels suchen..."}
                                    value={labelSearch}
                                    onChange={(e) => {
                                        setLabelSearch(e.target.value);
                                        setLabelDropdownOpen(true);
                                    }}
                                    onFocus={() => setLabelDropdownOpen(true)}
                                    disabled={projectLabelsLoading}
                                />
                                {labelDropdownOpen && filteredLabels.length > 0 && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            zIndex: 1050,
                                            top: "100%",
                                            left: 0,
                                            right: 0,
                                            maxHeight: "200px",
                                            overflowY: "auto",
                                            border: "1px solid #ced4da",
                                            borderRadius: "0 0 4px 4px",
                                            backgroundColor: "#fff",
                                            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        {filteredLabels.map((label) => {
                                            const isSelected = selectedLabels.includes(label.name);
                                            return (
                                                <div
                                                    key={label.id}
                                                    onClick={() => toggleLabel(label.name)}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        padding: "6px 10px",
                                                        cursor: "pointer",
                                                        backgroundColor: isSelected ? "#f0f0f0" : "#fff",
                                                    }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isSelected ? "#e8e8e8" : "#f8f8f8")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isSelected ? "#f0f0f0" : "#fff")}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        readOnly
                                                        style={{ marginRight: "8px", cursor: "pointer" }}
                                                    />
                                                    <span
                                                        style={{
                                                            display: "inline-block",
                                                            width: "12px",
                                                            height: "12px",
                                                            borderRadius: "50%",
                                                            backgroundColor: label.color || "var(--border-hairline)",
                                                            marginRight: "8px",
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span style={{ fontSize: "13px" }}>{label.name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <FormText color="muted">
                                &quot;Bug&quot; und &quot;Status::Backlog&quot; werden automatisch hinzugef{"\u00FC"}gt.
                            </FormText>
                        </FormGroup>
                        {!performanceSubmitting && bugReportErrorText && (
                            <AlertComponent errorMessages={bugReportErrorText} />
                        )}
                        <FormGroup style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "flex-end" }}>
                            <LoadingSpinnerButton
                                loading={!!bugReportLoading && !performanceSubmitting}
                                type="submit"
                                disabled={!bugTitle.trim() || !!bugReportLoading}
                            >
                                Bug melden
                            </LoadingSpinnerButton>
                            <PathwayButton color="secondary" disabled={bugReportLoading} onClick={handleClose}>
                                Abbrechen
                            </PathwayButton>
                        </FormGroup>
                    </Form>
                    </>
                )}

                {bugReportResult && (
                    <div>
                        <AlertComponent
                            successMessages={`GitLab Issue #${bugReportResult.issueIid} wurde erstellt. Customer Value: ${bugReportResult.customerValue}`}
                        />
                        <div style={{ marginBottom: "1rem" }}>
                            <a
                                href={bugReportResult.issueUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary"
                            >
                                <i className="fa fa-external-link" style={{ marginRight: "0.5rem" }} />
                                Issue #{bugReportResult.issueIid} in GitLab {"\u00F6"}ffnen
                            </a>
                        </div>
                        <PathwayButton color="secondary" onClick={handleClose}>
                            Schlie{"\u00DF"}en
                        </PathwayButton>
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};
